"use client";

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import Aurora from "@/components/dubai/ui/Aurora";

const benefitCards = [
  "Business owner visa support and application assistance",
  "Remote company setup with digital document submission",
  "100% foreign ownership available for eligible businesses",
  "UAE residency visa and Emirates ID assistance",
  "Access to a lower-tax business environment",
  "Company setup completed in as little as 7 days.",
];

const cardLayout: { side: "left" | "right" }[] = [
  { side: "left" },
  { side: "right" },
  { side: "left" },
  { side: "right" },
  { side: "left" },
  { side: "right" },
];

type DistortedTextPosition = {
  overlapsText: boolean;
  heading: { left: number; top: number; width: number };
  description: { left: number; top: number; width: number };
};

export const WhyEntrepreneursPreferIfzaSection = () => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<Array<HTMLDivElement | null>>([]);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const descriptionRef = useRef<HTMLParagraphElement>(null);
  const [progress, setProgress] = useState(0);
  const [mobileRevealed, setMobileRevealed] = useState(false);
  const [cardDepth, setCardDepth] = useState<number[]>(() =>
    benefitCards.map(() => 0),
  );
  const textApproach = mobileRevealed ? 1 : Math.min(1, progress / 0.3);
  const [backgroundTextPositions, setBackgroundTextPositions] = useState<
    Array<DistortedTextPosition | null>
  >(() => benefitCards.map(() => null));

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setMobileRevealed(true);
          observer.disconnect();
        }
      },
      { threshold: 0.12 },
    );

    observer.observe(wrapper);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let ticking = false;

    const updateProgress = () => {
      ticking = false;
      const wrapper = wrapperRef.current;
      if (!wrapper) return;

      const rect = wrapper.getBoundingClientRect();
      const scrollableDistance = rect.height - window.innerHeight;
      const scrolled = -rect.top;
      const next =
        scrollableDistance > 0
          ? Math.min(1, Math.max(0, scrolled / scrollableDistance))
          : 0;

      setProgress(next);

      const focusLine = window.innerHeight * 0.58;
      const depthRange = window.innerHeight * 0.72;
      const nextDepth = cardRefs.current.map((card) => {
        if (!card) return 0;
        const rect = card.getBoundingClientRect();
        const distance = Math.abs(rect.top + rect.height / 2 - focusLine);
        return Math.min(1, Math.max(0, 1 - distance / depthRange));
      });

      setCardDepth((current) =>
        nextDepth.some((depth, index) => Math.abs(depth - current[index]) > 0.015)
          ? nextDepth
          : current,
      );
    };

    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(updateProgress);
      }
    };

    updateProgress();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    let trackingFrame = 0;
    const trackVisibleSection = () => {
      const wrapper = wrapperRef.current;
      if (wrapper) {
        const rect = wrapper.getBoundingClientRect();
        if (rect.bottom >= -window.innerHeight && rect.top <= window.innerHeight * 2) {
          updateProgress();
        }
      }
      trackingFrame = requestAnimationFrame(trackVisibleSection);
    };
    trackingFrame = requestAnimationFrame(trackVisibleSection);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      cancelAnimationFrame(trackingFrame);
    };
  }, []);

  useLayoutEffect(() => {
    const headingRect = headingRef.current?.getBoundingClientRect();
    const descriptionRect = descriptionRef.current?.getBoundingClientRect();
    if (!headingRect || !descriptionRect) return;

    setBackgroundTextPositions(
      cardRefs.current.map((card) => {
        if (!card) return null;
        const cardRect = card.getBoundingClientRect();
        const localScaleX = cardRect.width > 0 ? card.offsetWidth / cardRect.width : 1;
        const localScaleY = cardRect.height > 0 ? card.offsetHeight / cardRect.height : 1;
        const intersects = (textRect: DOMRect) =>
          textRect.right > cardRect.left &&
          textRect.left < cardRect.right &&
          textRect.bottom > cardRect.top &&
          textRect.top < cardRect.bottom;

        return {
          overlapsText: intersects(headingRect) || intersects(descriptionRect),
          heading: {
            left: (headingRect.left - cardRect.left) * localScaleX,
            top: (headingRect.top - cardRect.top) * localScaleY,
            width: headingRect.width,
          },
          description: {
            left: (descriptionRect.left - cardRect.left) * localScaleX,
            top: (descriptionRect.top - cardRect.top) * localScaleY,
            width: descriptionRect.width,
          },
        };
      }),
    );
  }, [progress, cardDepth]);

  return (
    <div ref={wrapperRef} className="relative h-auto w-full lg:h-[400vh]">
      <section
        aria-labelledby="why-entrepreneurs-prefer-ifza"
        className="relative z-10 flex w-full items-start overflow-hidden bg-black lg:sticky lg:top-0 lg:aspect-[120/77] lg:h-screen lg:min-h-0"
      >
        <div className="pointer-events-none absolute inset-0 z-0 opacity-30">
          <Aurora
            colorStops={["#0061FE", "#1991fe", "#0061FE"]}
            blend={0.52}
            amplitude={0.65}
            speed={1.3}
          />
        </div>
        <svg aria-hidden="true" className="pointer-events-none absolute h-0 w-0 overflow-hidden">
          <defs>
            {benefitCards.map((_, index) => (
              <filter
                key={index}
                id={`background-text-distortion-${index}`}
                x="-12%"
                y="-20%"
                width="124%"
                height="140%"
                colorInterpolationFilters="sRGB"
              >
                <feTurbulence
                  type="fractalNoise"
                  baseFrequency="0.009 0.018"
                  numOctaves="2"
                  seed={index + 8}
                  result="static-distortion-map"
                />
                <feDisplacementMap
                  in="SourceGraphic"
                  in2="static-distortion-map"
                  scale={8 + cardDepth[index] * 24}
                  xChannelSelector="R"
                  yChannelSelector="G"
                />
              </filter>
            ))}
          </defs>
        </svg>
        <div className="animate-ellipse-rise absolute left-[-13.194%] top-[-16.342%] hidden h-[45.022%] w-[34.792%] rounded-[336px] bg-[radial-gradient(50%_50%_at_50%_50%,rgba(0,97,254,0.2)_0%,rgba(0,97,254,0)_68%)] blur-[12.5px] lg:block" />
        <div className="relative z-20 flex w-full flex-col items-center gap-10 px-4 pb-12 pt-12 sm:gap-12 sm:pt-16 lg:absolute lg:inset-0 lg:block lg:gap-0 lg:px-0 lg:py-0">
          <div className="flex w-full flex-col items-center gap-6 lg:absolute lg:left-1/2 lg:top-1/2 lg:w-[90.625%] lg:-translate-x-1/2 lg:-translate-y-1/2 lg:gap-8">
            <div className={`inline-flex items-center justify-center gap-2 transition-[transform,opacity] duration-700 sm:gap-4 ${mobileRevealed ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"}`}>
              <img
                src="/dubai/left-design.svg"
                alt=""
                aria-hidden="true"
                className="hidden h-[6px] w-[114px] sm:block"
              />
              <div className="inline-flex items-center gap-2 rounded-[99px] border border-[#0B3CFF4c] bg-[#0a1530] px-1.5 py-1 pr-2.5 backdrop-blur-[2.5px]">
                <img src="/dubai/box.svg" alt="" aria-hidden="true" className="h-5 w-5" />
                <span className="font-suisse-intl text-sm font-light leading-[17px] text-blue-200">
                  Why IFZA
                </span>
              </div>
              <img
                src="/dubai/right-design.svg"
                alt=""
                aria-hidden="true"
                className="hidden h-[6px] w-[114px] sm:block"
              />
            </div>
            <div
              className="flex w-full flex-col items-center gap-6 transition-[transform,opacity,filter] duration-1000 ease-out"
              style={{
                transform: `perspective(1200px) translate3d(0, ${(1 - textApproach) * 52}px, ${-(1 - textApproach) * 220}px) scale(${0.72 + textApproach * 0.28})`,
                transformOrigin: "center center",
                opacity: 0.42 + textApproach * 0.58,
                filter: `blur(${(1 - textApproach) * 3}px)`,
                willChange: "transform, opacity, filter",
              }}
            >
              <h1
                ref={headingRef}
                id="why-entrepreneurs-prefer-ifza"
                className="z-10 w-[96%] bg-[linear-gradient(180deg,#1991fe_0%,#DAE8FF_20%,#1991fe_40%,#DAE8FF_100%)] bg-clip-text text-center font-suisse-intl text-[clamp(32px,8vw,70px)] font-normal leading-[1.15] text-transparent lg:w-[63.793%] lg:text-[clamp(13px,4.861vw,70px)] lg:leading-[1.2]"
              >
                Why Entrepreneurs <br></br> Prefer IFZA Dubai
              </h1>
              <p ref={descriptionRef} className="z-10 w-[94%] text-center font-suisse-intl text-sm leading-[1.5] text-white/80 sm:text-base lg:w-[73.793%] lg:text-[clamp(6px,1.389vw,20px)] lg:leading-[1.6]">
                IFZA offers a practical Dubai company setup solution for SaaS
                founders,<br/> e-commerce sellers,
               
                agencies, consultants, course creators, <br /> and other digital
                businesses.
              </p>
            </div>
          </div>

          <div className="flex w-full flex-col gap-3 lg:hidden">
            {benefitCards.map((title, index) => (
              <div
                key={`mobile-${title}`}
                className={`flex items-start gap-3 rounded-2xl bg-[linear-gradient(145deg,rgba(8,12,25,0.96),rgba(3,4,9,0.94))] p-4 text-white shadow-[0_16px_36px_rgba(0,0,0,0.28)] transition-[transform,opacity] duration-700 ease-out ${mobileRevealed ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}
                style={{ transitionDelay: `${index * 90}ms` }}
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-white/50 text-sm font-semibold">
                  {index + 1}
                </span>
                <p className="m-0 pt-1 font-suisse-intl text-base font-semibold leading-6">
                  {title}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="absolute inset-x-0 top-0 mx-auto max-w-[1040px] lg:inset-0">
          <div
            style={{ top: `${136 - progress * 200}vh` }}
            className="absolute flex w-full flex-col gap-3"
          >
            {benefitCards.map((title, i) => {
              const layout = cardLayout[i];
              return (
                <div
                  key={title + i}
                  ref={(element) => {
                    cardRefs.current[i] = element;
                  }}
                  className={`relative z-30 hidden w-[calc(100vw-32px)] max-w-[420px] items-start gap-2 self-center overflow-hidden rounded-[32px] border-0 bg-[linear-gradient(145deg,rgba(8,12,25,0.94),rgba(3,4,9,0.9))] p-6 backdrop-blur-[12px] [-webkit-backdrop-filter:blur(12px)] sm:p-8 lg:flex lg:w-[420px] lg:max-w-none lg:p-8 ${
                    layout.side === "left"
                      ? "lg:self-start lg:ml-[6%]"
                      : "lg:self-end lg:mr-[6%]"
                  }`}
                  style={{
                    transform: `perspective(1100px) translateZ(${cardDepth[i] * 42}px) rotateX(${(0.5 - cardDepth[i]) * 7}deg) scale(${0.94 + cardDepth[i] * 0.08})`,
                    opacity: 0.58 + cardDepth[i] * 0.42,
                    boxShadow: `0 ${18 + cardDepth[i] * 24}px ${54 + cardDepth[i] * 54}px rgba(0,0,0,${0.42 + cardDepth[i] * 0.2}), 0 0 ${34 + cardDepth[i] * 44}px rgba(0,97,254,${0.2 + cardDepth[i] * 0.2}), inset 0 1px 0 rgba(255,255,255,${0.16 + cardDepth[i] * 0.18}), inset 0 -1px 0 rgba(111,166,255,0.16)`,
                    transition:
                      "transform 180ms ease-out, opacity 180ms ease-out, box-shadow 180ms ease-out",
                    transformStyle: "preserve-3d",
                    willChange: "transform, opacity",
                  }}
                >
                  {backgroundTextPositions[i]?.overlapsText && <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 z-10 overflow-hidden rounded-[inherit] bg-[linear-gradient(135deg,rgba(6,4,12,0.72),rgba(4,3,9,0.78)_42%,rgba(0,0,0,0.88))] backdrop-blur-[18px] [-webkit-backdrop-filter:blur(18px)]"
                  >
                    <div
                      className="absolute inset-0"
                      style={{
                        opacity: 0.35 + cardDepth[i] * 0.2,
                        background:
                          "linear-gradient(180deg, rgba(255,255,255,0.03), transparent 35%)",
                      }}
                    />
                    <div
                      className="absolute m-0 w-[63.793%] bg-[radial-gradient(50%_50%_at_50%_50%,#0061FE_34.13%,#DAE8FF_100%)] bg-clip-text text-center font-suisse-intl text-[clamp(13px,4.861vw,70px)] font-normal leading-[1.2] text-transparent"
                      style={{
                        left: backgroundTextPositions[i]!.heading.left,
                        top: backgroundTextPositions[i]!.heading.top,
                        width: backgroundTextPositions[i]!.heading.width,
                        opacity: 0.32 + cardDepth[i] * 0.18,
                        filter: `url(#background-text-distortion-${i}) blur(${1.5 - cardDepth[i] * 0.5}px)`,
                        textShadow: "0 1px 8px rgba(255,255,255,0.55)",
                        willChange: "filter, opacity",
                      }}
                    >
                      Why Entrepreneurs <br /> Prefer IFZA Dubai
                    </div>
                    <p
                      className="absolute m-0 w-[63.793%] text-center font-suisse-intl text-[clamp(6px,1.389vw,20px)] font-normal leading-[1.6] text-white/80"
                      style={{
                        left: backgroundTextPositions[i]!.description.left,
                        top: backgroundTextPositions[i]!.description.top,
                        width: backgroundTextPositions[i]!.description.width,
                        opacity: 0.24 + cardDepth[i] * 0.14,
                        filter: `url(#background-text-distortion-${i}) blur(${1.5 - cardDepth[i] * 0.5}px)`,
                        textShadow: "0 1px 7px rgba(255,255,255,0.4)",
                        willChange: "filter, opacity",
                      }}
                    >
                      IFZA offers a practical Dubai company setup solution for
                      SaaS founders, e-commerce sellers,
                      <br />
                      agencies, consultants, course creators, and other digital
                      businesses.
                    </p>
                    <div
                      className="absolute inset-0"
                      style={{
                        background:
                          "linear-gradient(180deg, rgba(2,1,6,0.62), rgba(2,1,6,0.78))",
                        opacity: 0.72 + cardDepth[i] * 0.18,
                      }}
                    />
                  </div>}
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute left-[18%] top-[-140%] h-[240%] w-[85%] rounded-[336px] bg-[radial-gradient(ellipse_70.71%_70.71%_at_50%_50%,rgba(0,97,254,0.2)_0%,rgba(255,255,255,0)_68%)] blur-[12.5px]"
                  />
                  <div className="relative z-10 flex h-[44.8px] w-[44.8px] shrink-0 flex-col items-center justify-center pb-[13.54px] pt-[13.26px]">
                    <span className="font-suisse-intl text-[11.5px] font-extrabold leading-[17.28px] text-white">
                      {i + 1}
                    </span>
                    <div className="absolute left-[1.87px] top-[1.87px] h-[41.07px] w-[41.07px] rounded-full [outline:0.93px_solid_white] [outline-offset:-0.47px]" />
                  </div>
                  <p className="relative z-10 flex-1 font-suisse-intl text-base font-medium leading-6 text-[#faf9fa] sm:text-xl sm:leading-[25.92px]">
                    {title}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};
